import React from 'react';
import {isAxiosError} from 'axios';
import {useNavigate} from 'react-router-dom';
import {ConfirmationModal, ConfirmationSuccessIcon} from '@rocket-ui/components';
import {fetchBanks, useCreatePaymentWay, useFetchPaymentWays} from '@rocket-ui/lk-api';
import {ROUTES} from 'app/router/routes';
import {
	CARD_BIN_LENGTH,
	CARD_PAYMENT_SUCCESS_DESCRIPTION,
	CARD_PAYMENT_SUCCESS_TITLE,
	CardNumberStep,
	ProfileAlertsBanner,
	CardReviewStep,
	StepProgress,
	bankFromApi,
	buildCardPaymentWayRequest,
	extractCardBin,
	isCardNumberValid,
	type SelectedBank,
} from 'features/profile';
import {RegistrationButton, RegistrationConfirmationModal} from 'features/registration';
import styles from './AddAccountPaymentPage.module.css';

type CardStep = 'card' | 'review';

const STEPS: CardStep[] = ['card', 'review'];

const BANK_NOT_FOUND_MESSAGE = 'Не удалось определить банк по номеру карты';

const backIcon = (
	<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
		<path d="M10 6L4 12L10 18" stroke="currentColor" strokeWidth="1.6" />
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M20 12.9135L5 12.9135L5 11.1992L20 11.1992L20 12.9135Z"
			fill="currentColor"
		/>
	</svg>
);

const closeIcon = (
	<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
		<path d="M3.99805 3.99805L20.0004 20.0004" stroke="currentColor" strokeWidth="1.6" />
		<path d="M3.99805 20L20.0004 3.99768" stroke="currentColor" strokeWidth="1.6" />
	</svg>
);

function getCreatePaymentWayErrorMessage(error: unknown): string {
	if (isAxiosError<{errors?: string[]; message?: string; detail?: string}>(error)) {
		return (
			error.response?.data?.errors?.[0] ||
			error.response?.data?.message ||
			error.response?.data?.detail ||
			error.message
		);
	}

	return error instanceof Error ? error.message : 'Не удалось добавить способ оплаты';
}

export function AddCardPaymentPage(): React.ReactElement {
	const navigate = useNavigate();
	const {data: paymentWays = []} = useFetchPaymentWays();
	const {mutateAsync: createPaymentWay, isLoading: isCreating} = useCreatePaymentWay();

	const [step, setStep] = React.useState<CardStep>('card');
	const [card, setCard] = React.useState('');
	const [selectedBank, setSelectedBank] = React.useState<SelectedBank | null>(null);
	const [isPrimary, setIsPrimary] = React.useState(false);
	const [submitError, setSubmitError] = React.useState<string | null>(null);
	const [isLookingUpBank, setIsLookingUpBank] = React.useState(false);
	const [isExitConfirmOpen, setIsExitConfirmOpen] = React.useState(false);
	const [isSuccessOpen, setIsSuccessOpen] = React.useState(false);

	const hasExistingPaymentWays = paymentWays.some((way) => way.visible !== false);
	const showPrimaryToggle = hasExistingPaymentWays;
	const stepIndex = STEPS.indexOf(step);
	const showBack = step !== 'card';
	const isBusy = isLookingUpBank || isCreating;

	const canContinue =
		!isBusy &&
		!isSuccessOpen &&
		((step === 'card' && isCardNumberValid(card)) || (step === 'review' && selectedBank !== null));

	React.useEffect(() => {
		if (!hasExistingPaymentWays) {
			setIsPrimary(true);
		}
	}, [hasExistingPaymentWays]);

	const handleCloseRequest = () => {
		if (isBusy || isSuccessOpen) {
			return;
		}

		setIsExitConfirmOpen(true);
	};

	const handleConfirmExit = () => {
		setIsExitConfirmOpen(false);
		navigate(ROUTES.profile);
	};

	const handleSuccessContinue = () => {
		setIsSuccessOpen(false);
		navigate(ROUTES.profile);
	};

	const handleBack = () => {
		if (isBusy || isSuccessOpen) {
			return;
		}

		if (step === 'review') {
			setStep('card');
			setSubmitError(null);
		}
	};

	const handleCardChange = (nextCard: string) => {
		setCard(nextCard);
		setSubmitError(null);
		setSelectedBank(null);
	};

	const lookupBankByBin = async (): Promise<SelectedBank | null> => {
		const bin = extractCardBin(card);

		if (bin.length < CARD_BIN_LENGTH) {
			return null;
		}

		const {data} = await fetchBanks({bin});
		const bank = data[0];

		return bank ? bankFromApi(bank) : null;
	};

	const handleContinue = async () => {
		if (!canContinue) {
			return;
		}

		if (step === 'card') {
			setSubmitError(null);
			setIsLookingUpBank(true);

			try {
				const bank = await lookupBankByBin();

				if (!bank) {
					setSubmitError(BANK_NOT_FOUND_MESSAGE);
					return;
				}

				setSelectedBank(bank);
				setStep('review');
			} catch (error) {
				setSubmitError(getCreatePaymentWayErrorMessage(error) || BANK_NOT_FOUND_MESSAGE);
			} finally {
				setIsLookingUpBank(false);
			}

			return;
		}

		const request = buildCardPaymentWayRequest({
			card,
			selectedBank,
			isPrimary: hasExistingPaymentWays ? isPrimary : true,
		});

		if (!request) {
			setSubmitError('Проверьте номер карты и банк');
			return;
		}

		setSubmitError(null);

		try {
			await createPaymentWay(request);
			setIsSuccessOpen(true);
		} catch (error) {
			setSubmitError(getCreatePaymentWayErrorMessage(error));
		}
	};

	return (
		<div className={styles.root}>
			<header className={styles.header}>
				<div className={styles.headerInner}>
					{showBack ? (
						<button type="button" className={styles.backButton} onClick={handleBack} aria-label="Назад">
							{backIcon}
						</button>
					) : (
						<span className={styles.headerSpacer} aria-hidden />
					)}
					<StepProgress currentStep={stepIndex} totalSteps={STEPS.length} className={styles.progress} />
					<button
						type="button"
						className={styles.closeButton}
						onClick={handleCloseRequest}
						aria-label="Закрыть"
					>
						{closeIcon}
					</button>
				</div>
			</header>

			<div className={styles.content}>
				<div className={`${styles.contentInner} ${styles.contentInnerWhite}`}>
					<ProfileAlertsBanner />
					{step === 'card' ? <CardNumberStep value={card} onChange={handleCardChange} /> : null}

					{step === 'review' ? (
						<CardReviewStep
							card={card}
							bank={selectedBank}
							isPrimary={isPrimary}
							showPrimaryToggle={showPrimaryToggle}
							onPrimaryChange={setIsPrimary}
							error={submitError}
						/>
					) : null}

					{step === 'card' && submitError ? <p className={styles.errorText}>{submitError}</p> : null}

					<div className={styles.contentActions}>
						<RegistrationButton
							variant="primary"
							className={styles.continueButton}
							onClick={() => {
								void handleContinue();
							}}
							disabled={!canContinue}
							loading={isBusy}
						>
							{step === 'review' ? 'Всё верно' : 'Продолжить'}
						</RegistrationButton>
					</div>
				</div>
			</div>

			<ConfirmationModal
				isOpen={isExitConfirmOpen}
				title="Хотите выйти?"
				description="Вы уверены, что хотите прервать добавление реквизитов?"
				confirmText="Выйти"
				onConfirm={handleConfirmExit}
				onCancel={() => setIsExitConfirmOpen(false)}
			/>

			<RegistrationConfirmationModal
				isOpen={isSuccessOpen}
				title={CARD_PAYMENT_SUCCESS_TITLE}
				description={CARD_PAYMENT_SUCCESS_DESCRIPTION}
				confirmText="Продолжить"
				illustration={<ConfirmationSuccessIcon />}
				onConfirm={handleSuccessContinue}
				onCancel={handleSuccessContinue}
			/>
		</div>
	);
}
